/**
 * The party module's route table (design C5 §5.6).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `PARTY_ROUTES` in — which is why the wiring
 * packet's edit there is one import and one spread, and contains no judgement
 * about a module it did not build.
 *
 * **No route here gates on an entitlement.** §6 gives every `internal/party`
 * route `Module: ""` because a data-subject request is a compliance obligation
 * and not a purchasable feature. The **loyalty** routes are the one place C5
 * declares `Module: "crm"` server-side — loyalty is commercial and a tenant
 * without CRM has not bought it — but the enforcement is the server's
 * `ERR_MODULE_NOT_ENTITLED`, which these screens render like any other refusal;
 * `RouteMeta` carries no module field and inventing one here would be a second,
 * disagreeing gate.
 *
 * One ordering is load-bearing and is the trick AR and bankrec already use: a
 * literal segment (`new`) is declared **before** its `:id(\d+)` sibling so it
 * can never be parsed as a document id. The `\d+` constraint makes it safe
 * anyway; the ordering makes it obvious.
 */

import type { RouteRecordRaw } from 'vue-router'

import { PARTY_PERMISSIONS } from '@/modules/party/permissions'

export const PARTY_ROUTES: RouteRecordRaw[] = [
  {
    path: '/party/parties',
    name: 'party-parties',
    component: () => import('@/modules/party/parties/PartiesListView.vue'),
    meta: { titleKey: 'party.routeTitles.parties', permission: PARTY_PERMISSIONS.read },
  },
  {
    path: '/party/parties/:id(\\d+)',
    name: 'party-party',
    component: () => import('@/modules/party/parties/PartyView.vue'),
    meta: { titleKey: 'party.routeTitles.party', permission: PARTY_PERMISSIONS.read },
  },
  {
    path: '/party/parties/:id(\\d+)/consent',
    name: 'party-consent-matrix',
    component: () => import('@/modules/party/consent/ConsentMatrixView.vue'),
    meta: { titleKey: 'party.routeTitles.matrix', permission: PARTY_PERMISSIONS.consentRead },
  },
  {
    path: '/party/merges',
    name: 'party-merges',
    component: () => import('@/modules/party/merges/MergesListView.vue'),
    meta: { titleKey: 'party.routeTitles.merges', permission: PARTY_PERMISSIONS.read },
  },
  {
    path: '/party/merges/:id(\\d+)',
    name: 'party-merge',
    component: () => import('@/modules/party/merges/MergeWorkbenchView.vue'),
    meta: { titleKey: 'party.routeTitles.merge', permission: PARTY_PERMISSIONS.read },
  },
  {
    path: '/party/consent/catalogues',
    name: 'party-consent-catalogues',
    component: () => import('@/modules/party/consent/ConsentCataloguesView.vue'),
    meta: { titleKey: 'party.routeTitles.catalogues', permission: PARTY_PERMISSIONS.consentRead },
  },
  {
    path: '/party/consent/records',
    name: 'party-consent-records',
    component: () => import('@/modules/party/consent/ConsentRecordsView.vue'),
    meta: { titleKey: 'party.routeTitles.consents', permission: PARTY_PERMISSIONS.consentRead },
  },
  {
    path: '/party/suppressions',
    name: 'party-suppressions',
    component: () => import('@/modules/party/suppressions/SuppressionsView.vue'),
    meta: {
      titleKey: 'party.routeTitles.suppressions',
      permission: PARTY_PERMISSIONS.suppressionRead,
    },
  },
  {
    path: '/party/dsr',
    name: 'party-dsr-board',
    component: () => import('@/modules/party/dsr/DSRBoardView.vue'),
    meta: { titleKey: 'party.routeTitles.dsrBoard', permission: PARTY_PERMISSIONS.dsrRead },
  },
  {
    path: '/party/dsr/:id(\\d+)',
    name: 'party-dsr',
    component: () => import('@/modules/party/dsr/DSRRequestView.vue'),
    meta: { titleKey: 'party.routeTitles.dsr', permission: PARTY_PERMISSIONS.dsrRead },
  },
  {
    path: '/party/retention',
    name: 'party-retention',
    component: () => import('@/modules/party/retention/RetentionCatalogueView.vue'),
    meta: { titleKey: 'party.routeTitles.retention', permission: PARTY_PERMISSIONS.retentionRead },
  },
  {
    path: '/party/loyalty/programs',
    name: 'party-loyalty-programs',
    component: () => import('@/modules/party/loyalty/LoyaltyProgramsView.vue'),
    meta: {
      titleKey: 'party.routeTitles.loyaltyPrograms',
      permission: PARTY_PERMISSIONS.loyaltyRead,
    },
  },
  {
    path: '/party/loyalty/programs/:id(\\d+)',
    name: 'party-loyalty-program',
    component: () => import('@/modules/party/loyalty/LoyaltyProgramView.vue'),
    meta: {
      titleKey: 'party.routeTitles.loyaltyProgram',
      permission: PARTY_PERMISSIONS.loyaltyRead,
    },
  },
  {
    path: '/party/loyalty/members',
    name: 'party-loyalty-members',
    component: () => import('@/modules/party/loyalty/LoyaltyMembersView.vue'),
    meta: {
      titleKey: 'party.routeTitles.loyaltyMembers',
      permission: PARTY_PERMISSIONS.loyaltyRead,
    },
  },
  {
    path: '/party/loyalty/members/:id(\\d+)',
    name: 'party-loyalty-member',
    component: () => import('@/modules/party/loyalty/LoyaltyMemberView.vue'),
    meta: {
      titleKey: 'party.routeTitles.loyaltyMember',
      permission: PARTY_PERMISSIONS.loyaltyRead,
    },
  },
]
